import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { translate } from '../../locales';

import PhoneInput from '../../components/PhoneInput';
import ForgotPasswordModal from '../ForgotPasswordModal';
import RegisterModal from '../RegisterModal';
import './style.css';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { login, loginWithGoogle } = useAuth();
  const { language } = useLanguage();
  
  const [loginType, setLoginType] = useState<'email' | 'phone'>('phone');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+380');
  const [phone, setPhone] = useState('');
  const [fullPhone, setFullPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const [acceptTerms, setAcceptTerms] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const t = (key: any) => translate(key, language as 'en' | 'ua');



  const handlePhoneInputChange = (fullPhone: string, code: string, localNumber: string) => {
    setFullPhone(fullPhone);
    setCountryCode(code);
    setPhone(localNumber);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Підготовка даних для логіну
      const loginData = loginType === 'email'
        ? { email, password }
        : { phone: fullPhone, password };

      await login(loginData);
      onClose();
    } catch (err: any) {
      setError(err.message || t('auth.error.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      // Використовуємо loginWithGoogle з AuthContext
      await loginWithGoogle();
      onClose();
    } catch (err: any) {
      setError(err.message || t('auth.error.loginFailed'));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };



  // Блокуємо прокрутку body коли модалка відкрита
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="login-modal-backdrop">
      <div className="login-modal">
        <button className="modal-close-btn" onClick={handleClose}>
          ✕
        </button>

        <div className="modal-header">
          <div className="modal-icon">
            <i className="bi bi-person-circle"></i>
          </div>
          <h2>{t('auth.login.title')}</h2>
          <p>{t('auth.login.subtitle')}</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="error-message">
              <i className="bi bi-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Перемикач типу логіну */}
          <div className="login-type-selector">
            <button
              type="button"
              className={`type-button ${loginType === 'phone' ? 'active' : ''}`}
              onClick={() => {
                setLoginType('phone');
                // Очищаємо поля при перемиканні та встановлюємо дефолтний номер
                setEmail('');
                setPhone('');
                setPassword('');
                setError('');
              }}
              title={t('auth.loginType.phoneTitle')}
            >
              <i className="bi bi-phone"></i>
              <span className="button-text">{t('auth.loginType.phone')}</span>
            </button>
            <button
              type="button"
              className={`type-button ${loginType === 'email' ? 'active' : ''}`}
              onClick={() => {
                setLoginType('email');
                // Очищаємо поля при перемиканні
                setEmail('');
                setPhone('');
                setPassword('');
                setError('');
              }}
              title={t('auth.loginType.emailTitle')}
            >
              <i className="bi bi-envelope"></i>
              <span className="button-text">{t('auth.loginType.email')}</span>
            </button>
          </div>

          {/* Email або телефон */}
          {loginType === 'email' ? (
            <div className="form-group">
              <label htmlFor="email">
                <i className="bi bi-envelope"></i>
                {t('auth.login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.login.emailPlaceholder')}
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="phone">
                <i className="bi bi-phone"></i>
                {t('auth.loginType.phoneLabel')}
              </label>
              <PhoneInput
                id="phone"
                countryCode={countryCode}
                localNumber={phone}
                onChange={handlePhoneInputChange}
                placeholder={t('auth.loginType.phonePlaceholder')}
                disabled={isLoading}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="password">
              <i className="bi bi-lock"></i>
              {t('auth.login.password')}
            </label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.login.passwordPlaceholder')}
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
              >
                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="forgot-password-link"
              style={{ marginLeft: 'auto' }}
            >
              {t('auth.login.forgotPassword')}
            </button>
          </div>

          <div className="form-footer">
            <label className="remember-me">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
              <span>
                {t('auth.terms.acceptance')}{' '}
                <a href="/public-offer" target="_blank" rel="noopener noreferrer" className="link">
                  {t('auth.terms.termsAndRules')}
                </a>
                {t('auth.terms.andText')}{' '}
                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link">
                  {t('auth.terms.privacyNotice')}
                </a>
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={isLoading || !acceptTerms}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                {t('auth.login.loggingIn')}
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right"></i>
                {t('auth.login.loginButton')}
              </>
            )}
          </button>

          <div className="oauth-separator">
            <span>{t('auth.oauth.or')}</span>
          </div>

          <div className="oauth-buttons">
            <button
              type="button"
              className="google-btn"
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading || !acceptTerms}
            >
              {isGoogleLoading ? (
                <>
                  <span className="spinner"></span>
                  {t('auth.oauth.google')}
                </>
              ) : (
                <>
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                  {t('auth.oauth.continueWithGoogle')}
                </>
              )}
            </button>
          </div>

          <div className="signup-link">
            {t('auth.login.noAccount')}
            <button 
              type="button"
              onClick={() => setShowRegister(true)}
              className="signup-link-btn"
            >
              {t('auth.login.signUp')}
            </button>
          </div>
        </form>
      </div>

      {/* ForgotPasswordModal поверх LoginModal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
      
      {/* RegisterModal поверх LoginModal */}
      <RegisterModal 
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onOpenLogin={() => {
          setShowRegister(false);
          // Модалка входу вже відкрита, не потрібно нічого робити
        }}
      />
    </div>
  );
};

export default LoginModal;
